// Client for the headed .exe QA agent flow (the /exe/* package — separate tables,
// fully isolated from /test and /extension). Mirrors api/extension.js conventions.
// Live views POLL (getExeRun / getExeSuiteRun) because EventSource can't send the
// Bearer header; the backend SSE endpoints exist for future use.

import { api, filenameFromDisposition, getApiBase, getToken, qs } from "./client";

// ---- devices / system health --------------------------------------------
export const listExeDevices = () => api.get("/exe/devices");
export const getExeDevice = (id) => api.get(`/exe/devices/${id}`);
export const pingExeDevice = (id) => api.post(`/exe/devices/${id}/ping`);
export const renameExeDevice = (id, displayName) =>
  api.put(`/exe/devices/${id}/name`, { display_name: displayName });

// ---- runs ----------------------------------------------------------------
export const startExeRun = (payload) => api.post("/exe/run", payload);
export const getExeRun = (id) => api.get(`/exe/runs/${id}`);
// mine=true scopes to the caller's OWN runs (not the whole company) — used by
// the tester Runs & Bugs page so a tester sees only the cases they tested.
// standalone=true drops runs that belong to a batch item, leaving the Run Single
// runs — the batch-first History / Runs & Bugs screens list those separately.
export const listExeRuns = (limit = 50, { mine = false, standalone = false } = {}) =>
  api.get("/exe/runs", {
    params: { limit, mine: mine || undefined, standalone: standalone || undefined },
  });
// History → Delete all: remove every run in the caller's scope.
export const deleteAllExeRuns = () => api.del("/exe/runs");
export const cancelExeRun = (id) => api.post(`/exe/runs/${id}/cancel`);
export const compileExeRun = (id, name) =>
  api.post(`/exe/runs/${id}/compile`, { name });
export const rootCauseExeRun = (id) => api.post(`/exe/runs/${id}/root-cause`);
export const preflightExe = (payload) => api.post("/exe/preflight", payload);

// ---- deterministic tests -------------------------------------------------
export const listExeTests = () => api.get("/exe/tests");
export const getExeTest = (id) => api.get(`/exe/tests/${id}`);
export const replayExeTest = (testId, deviceId, browser = "chromium") =>
  api.post(`/exe/tests/${testId}/replay`, null, {
    params: { device_id: deviceId, browser },
  });
// Saved Tests → Delete all: remove every saved test in the caller's scope.
export const deleteAllExeTests = () => api.del("/exe/tests");

// ---- multi-test suites ---------------------------------------------------
export const listExeSuites = () => api.get("/exe/suites");
export const createExeSuite = (payload) => api.post("/exe/suites", payload);
export const getExeSuite = (id) => api.get(`/exe/suites/${id}`);
export const runExeSuite = (id, payload) =>
  api.post(`/exe/suites/${id}/run`, payload);
export const getExeSuiteRun = (id) => api.get(`/exe/suite-runs/${id}`);
// Suites → Delete all: remove every suite in the caller's scope.
export const deleteAllExeSuites = () => api.del("/exe/suites");

// ---- imported test cases + batch distribution ----------------------------
export const detectExeColumns = (columns, samples) =>
  api.post("/exe/test-cases/detect-columns", { columns, samples });
export const importExeTestCases = (payload) =>
  api.post("/exe/test-cases/import", payload);
// Attach existing cases to a project (or pass null to detach). Only FUTURE
// batches inherit it — a batch records its project when it is created.
export const moveExeTestCases = (caseIds, projectId) =>
  api.post("/exe/test-cases/move", { case_ids: caseIds, project_id: projectId || null });
// One page of the catalog. The endpoint pages and returns {items, total, limit, offset};
// older builds answer with a plain array, so both shapes are normalised here.
export const listExeTestCases = (limit = 500) =>
  api.get("/exe/test-cases", { params: { limit } }).then((r) => (Array.isArray(r) ? r : r?.items || []));
export const pageExeTestCases = (params = {}) =>
  api.get(`/exe/test-cases${qs(params)}`);

// The WHOLE catalog. A single capped request truncated the list in silence — 371 of
// 871 imported cases were never displayable or selectable and two sheets never ran —
// so keep asking until the server runs out of rows. `total` is the server's own count
// when it reports one, so the screen can show loaded-vs-imported instead of hiding the
// gap. maxItems is a stop so a misbehaving endpoint can't spin here forever.
export async function loadAllExeTestCases({ pageSize = 500, maxItems = 20000 } = {}) {
  const items = [];
  const seen = new Set();
  let total = null;
  let offset = 0;
  while (offset < maxItems) {
    const res = await pageExeTestCases({ limit: pageSize, offset });
    const page = Array.isArray(res) ? res : res?.items || [];
    if (!Array.isArray(res) && typeof res?.total === "number") total = res.total;
    // Pages are ordered by created_at, which can tie across a bulk import — drop ids
    // already held so a row can't be listed (and React-keyed) twice.
    let added = 0;
    for (const c of page) {
      if (c && !seen.has(c.id)) { seen.add(c.id); items.push(c); added += 1; }
    }
    offset += page.length;
    // A short page is the end of the list. `added === 0` also stops a backend that
    // ignores `offset` and would otherwise hand back page one forever.
    if (page.length < pageSize || added === 0) break;
  }
  return { items, total: total == null ? items.length : total };
}
export const deleteExeTestCase = (id) => api.del(`/exe/test-cases/${id}`);
// Import Cases → Delete all: remove every imported case in the caller's scope.
export const deleteAllExeTestCases = () => api.del("/exe/test-cases");
// Convert cases that carry a COPIED login into ones that carry its NAME, so the agent
// signs in with the project's CURRENT password instead of the one frozen at push time.
// `projectId` is needed only for cases IMPORTED from a downloaded sheet: they carry
// a login but no project, so there is nowhere to read the current password from
// until they are told which project they belong to.
export const refreshExeLogins = (projectId) =>
  api.post("/exe/test-cases/refresh-logins", projectId ? { project_id: projectId } : {});

export const createExeBatch = (body) => api.post("/exe/batches", body);
// projectId narrows the query server-side. Fetching the newest N company-wide and
// filtering by project in the browser drops a quiet project's batches entirely.
// mine=true scopes to the caller's OWN batches (not the whole company /
// every assigned project) — the tester Runs & Bugs page uses it so its
// totals match the per-user figures on the home dashboard.
export const listExeBatches = (limit = 50, { projectId, mine = false } = {}) =>
  api.get("/exe/batches", {
    params: { limit, project_id: projectId || undefined, mine: mine || undefined },
  });
export const getExeBatch = (id) => api.get(`/exe/batches/${id}`);
// The endpoint now pages and returns {items, total, limit, offset}. This keeps
// returning a plain array so the existing screens are unaffected; use
// pageExeBatchItems when you need the total to page against.
export const listExeBatchItems = (id) =>
  api.get(`/exe/batches/${id}/items`).then((r) => (Array.isArray(r) ? r : r?.items || []));
export const pageExeBatchItems = (id, params = {}) =>
  api.get(`/exe/batches/${id}/items${qs(params)}`);
export const pauseExeBatch = (id) => api.post(`/exe/batches/${id}/pause`);
export const resumeExeBatch = (id) => api.post(`/exe/batches/${id}/resume`);
export const stopExeBatch = (id) => api.post(`/exe/batches/${id}/stop`);
export const takeoverExeBatch = (id) => api.post(`/exe/batches/${id}/takeover`);
// Running → Delete all: stop any live batches, then remove every batch in scope.
export const deleteAllExeBatches = () => api.del("/exe/batches");
// Clear all data: batches + runs + imported cases in the caller's scope.
export const clearAllExeData = () => api.del("/exe/data");

// Download the XLSX execution report for a batch (auth'd fetch → blob → browser download).
export async function downloadExeBatchReport(id) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const url = new URL(getApiBase() + `/exe/batches/${id}/report`, origin);
  const token = getToken();
  const res = await fetch(url.toString(), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Report download failed (${res.status})`);
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const filename = filenameFromDisposition(cd, "batch_report.xlsx");
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
  return { filename, sizeBytes: blob.size };
}

// ---- agent tokens (kind="exe") -------------------------------------------
export const createExeToken = (name) => api.post("/exe/tokens", { name });
export const listExeTokens = () => api.get("/exe/tokens");
export const revokeExeToken = (id) => api.post(`/exe/tokens/${id}/revoke`);

// Take a machine out of every OTHER tester's device picker. Only its owner (or a
// company admin) may set this — the server enforces that too.
export const setDevicePersonal = (deviceId, isPersonal) =>
  api.patch(`/exe/devices/${deviceId}/personal`, { is_personal: !!isPersonal });

// ---- download the agent --------------------------------------------------
export const getExeInfo = () => api.get("/exe/info");

// Upload a freshly-built agent .zip (raw body + upload progress). The Download button then
// serves it to testers — see POST /exe/agent-build. Uses XHR because fetch() can't report
// upload %; sends the File as the raw request body (no multipart dependency on the server).
export function uploadAgentBuild(file, onProgress) {
  return new Promise((resolve, reject) => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const url = new URL(getApiBase() + "/exe/agent-build", origin);
    const token = getToken();
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url.toString());
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Content-Type", "application/zip");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } catch (e) { resolve({}); }
      } else {
        let msg = `Upload failed (${xhr.status})`;
        try { const j = JSON.parse(xhr.responseText); if (j && j.detail) msg = j.detail; } catch (e) { /* ignore */ }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed — network error"));
    xhr.send(file);
  });
}

// Auth-protected blob download (a plain link can't send the Bearer header).
export async function downloadAgent() {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const url = new URL(getApiBase() + "/exe/download", origin);
  const token = getToken();
  const res = await fetch(url.toString(), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = "ai-qa-agent.zip";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
  return { sizeBytes: blob.size };
}

// ---- misc ----------------------------------------------------------------
export const getExeVersion = () => api.get("/exe/version");
export const exeScreenshotPath = (runId, step) =>
  `/exe/runs/${runId}/screenshots/${step}`;

export const isTerminalExe = (status) =>
  ["passed", "failed", "error", "cancelled", "inconclusive", "blocked"].includes(
    String(status || "").toLowerCase()
  );

// ---- Re-run (dev-fix verification cycle) ---------------------------------
// Re-runs ONLY what needs it: cases a developer claims to have fixed (to verify)
// plus the ones still failing. Already-passed untouched cases are skipped, which
// is where the time and token savings come from.
export const getExeRerunPreview = (id) => api.get(`/exe/batches/${id}/rerun-preview`);
export const rerunExeBatch = (id, body = {}) => api.post(`/exe/batches/${id}/rerun`, body);
export const retryExeItem = (batchId, itemId) =>
  api.post(`/exe/batches/${batchId}/items/${itemId}/retry`, {});
export const cancelExeItem = (batchId, itemId) =>
  api.post(`/exe/batches/${batchId}/items/${itemId}/cancel`, {});
